/**
 * Composable for checking project access and permissions
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { projectService } from '@/database/services'

export function useProjectAccess() {
  const authStore = useAuthStore()

  /**
   * Check if current user is a member of a project
   * @param {string} projectId - Project ID
   * @param {Object} project - Optional project object (to avoid extra API call)
   * @returns {Promise<boolean>} True if user is a project member
   */
  const isProjectMember = async (projectId: string, project?: any): Promise<boolean> => {
    if (!authStore.user?.uid && !authStore.user?.id) {
      return false
    }

    const userId = authStore.user?.uid || authStore.user?.id

    // Super admins have access to all projects
    if (authStore.userProfile?.role?.toLowerCase() === 'super_admin') {
      return true
    }

    try {
      // Use provided project or fetch it
      const projectData = project || await projectService.getById(projectId)
      
      if (!projectData) {
        return false
      }

      const members = projectData.members || []
      return members.includes(userId)
    } catch (error) {
      console.warn('Error checking project access:', error)
      return false
    }
  }

  /**
   * Check if current user can create/update/delete test cases in a project
   * REQUIREMENT 4: Only project members can CUD Test Cases
   * @param {string} projectId - Project ID
   * @param {Object} project - Optional project object
   * @returns {Promise<boolean>} True if user can CUD test cases
   */
  const canCUDTestCases = async (projectId: string, project?: any): Promise<boolean> => {
    return await isProjectMember(projectId, project)
  }

  /**
   * Check if current user can create/update/delete test runs in a project
   * REQUIREMENT 4: Only project members can CUD Test Runs
   * @param {string} projectId - Project ID
   * @param {Object} project - Optional project object
   * @returns {Promise<boolean>} True if user can CUD test runs
   */
  const canCUDTestRuns = async (projectId: string, project?: any): Promise<boolean> => {
    return await isProjectMember(projectId, project)
  }

  /**
   * Check if current user can CRUD bugs in a project
   * REQUIREMENT 4: Anyone can CRUD Bugs
   * @param {string} projectId - Project ID (not used but kept for consistency)
   * @returns {boolean} Always true for bugs
   */
  const canCRUDBugs = (projectId?: string): boolean => {
    // Anyone can CRUD bugs (authenticated users)
    return !!(authStore.user?.uid || authStore.user?.id)
  }

  return {
    isProjectMember,
    canCUDTestCases,
    canCUDTestRuns,
    canCRUDBugs
  }
}

